/**
 * Recommendation pipeline Stages 0–8 (docs/03).
 * Pure: no I/O. Does not call deriveGrace or getCardState.
 */

import {
  asBps,
  asCents,
  asMilliCentsPerPoint,
  asMilliPointsPerDollar,
  cashbackCents,
  fxFeeCents,
  monthlyInterestCents,
  pointsRewardCents,
  type Cents,
} from '../money';
import { businessDate, diffDays, type BusinessDate } from '../time';
import {
  applyBps,
  bpsProduct,
  minBps,
  sourceConfidenceBps,
  stalenessMultiplierBps,
} from './confidence';
import {
  emptyExplanationSlots,
  formatBpsAsPercent,
  formatCentsDisplay,
  generateExplanation,
} from './explain';
import { categoryFromObservations, type CategoryObservationInput } from './categoryObservations';
import type {
  BenefitInput,
  CapPeriodInput,
  CategorySource,
  GateFailure,
  MerchantInput,
  OfferInput,
  PriorityMode,
  PurchaseQueryInput,
  RankCardInput,
  RankCardsInput,
  RankedCard,
  RecommendationOutput,
  RewardRuleInput,
  ScoreTrace,
  SettingsInput,
  SpendCategory,
  Warning,
} from './types';

/** docs/02 §2 — fixed reward rules TTL. */
const REWARD_RULE_TTL_DAYS = 365;
/** docs/02 §2 — offers TTL. */
const OFFER_TTL_DAYS = 14;

type ResolvedCategory = {
  category: SpendCategory;
  source: CategorySource;
  confidence_bps: number;
  uncertain: boolean;
  intermediary: boolean;
};

function resolveCategory(
  query: PurchaseQueryInput,
  merchant: MerchantInput | null,
  observations: readonly CategoryObservationInput[],
): ResolvedCategory {
  const intermediaryHint = merchant?.is_intermediary === true || query.category === 'wallet_or_p2p';

  if (query.category !== null) {
    return {
      category: query.category,
      source: 'user',
      confidence_bps: 10000,
      uncertain: false,
      intermediary: intermediaryHint || query.category === 'wallet_or_p2p',
    };
  }

  const merchantId = merchant?.id ?? query.merchant_id;
  if (merchantId !== null) {
    const observed = categoryFromObservations(merchantId, query.channel, observations);
    if (observed !== null) {
      return {
        category: observed,
        source: 'observed',
        confidence_bps: sourceConfidenceBps('user_reported'),
        uncertain: false,
        intermediary: merchant?.is_intermediary === true || observed === 'wallet_or_p2p',
      };
    }
  }

  if (merchant !== null) {
    const byChannel =
      query.channel === 'online'
        ? merchant.online_category
        : query.channel === 'in_store'
          ? merchant.in_store_category
          : null;
    const cat = byChannel ?? merchant.default_category;
    if (cat !== null) {
      const inferred = merchant.category_confidence === 'inferred';
      const unverified = merchant.category_confidence === 'unverified';
      return {
        category: cat,
        source: 'merchant_default',
        confidence_bps: sourceConfidenceBps(merchant.category_confidence),
        uncertain: inferred || unverified,
        intermediary: merchant.is_intermediary || cat === 'wallet_or_p2p',
      };
    }
  }

  return {
    category: 'unknown',
    source: 'unknown',
    confidence_bps: 0,
    uncertain: true,
    intermediary: merchant?.is_intermediary === true,
  };
}

function ageDays(asOf: BusinessDate, observedAt: string | null): number | null {
  if (observedAt === null) return null;
  const day = businessDate(observedAt.length >= 10 ? observedAt.slice(0, 10) : observedAt);
  return diffDays(asOf, day);
}

function graceEligible(
  card: RankCardInput,
  query: PurchaseQueryInput,
  settings: SettingsInput,
): { ok: boolean; reason: string } {
  const status = card.eligibility.grace.status;
  const allow = query.allow_interest_bearing || settings.allow_interest_bearing_recommendations;
  if (!settings.require_active_grace_for_spending || allow) {
    return { ok: true, reason: '' };
  }
  if (status === 'active') return { ok: true, reason: '' };
  if (status === 'likely_active' && settings.treat_likely_active_as_eligible) {
    return { ok: true, reason: '' };
  }
  return {
    ok: false,
    reason: card.eligibility.grace.reason || `Grace status ${status} is not spend-eligible.`,
  };
}

function postPurchaseUtilBps(card: RankCardInput, amount: Cents): number | null {
  const limit = card.eligibility.state.observed.credit_limit_cents;
  if (limit === null || limit <= 0n) return null;
  const posted = card.eligibility.state.estimated_posted_balance ?? 0n;
  return Number(((posted + amount) * 10000n) / limit);
}

function hardGates(
  card: RankCardInput,
  query: PurchaseQueryInput,
  settings: SettingsInput,
  warnings: Warning[],
): GateFailure[] {
  const fails: GateFailure[] = [];
  const capacity = card.eligibility.capacity.estimated_available_credit;
  const amount = query.amount_cents;

  // Lifecycle
  if (card.status === 'closed') {
    fails.push({
      code: 'ACCOUNT_CLOSED',
      severity: 'exclude',
      reason: 'Account is closed.',
    });
  } else if (card.status === 'suspended' || card.status === 'restricted') {
    fails.push({
      code: 'ACCOUNT_SUSPENDED',
      severity: 'exclude',
      reason: `Account status is ${card.status}.`,
    });
  } else if (card.status === 'not_activated') {
    fails.push({
      code: 'NOT_ACTIVATED',
      severity: 'exclude',
      reason: 'Account is not activated.',
    });
  }

  if (card.spend_policy === 'blocked') {
    fails.push({
      code: 'DO_NOT_USE',
      severity: 'exclude',
      reason: 'spend_policy is blocked.',
    });
  } else if (card.spend_policy === 'payment_only' || card.spend_policy === 'maintenance_only') {
    fails.push({
      code: 'NOT_SPEND_ELIGIBLE',
      severity: 'exclude',
      reason: `spend_policy is ${card.spend_policy}.`,
    });
  }

  // Network compatibility
  if (card.network === 'closed_loop') {
    const allowed = (card.closed_loop_merchant ?? '').trim().toLowerCase();
    const hay = `${query.merchant_input} ${query.merchant_id ?? ''}`.toLowerCase();
    if (allowed.length === 0 || !hay.includes(allowed)) {
      fails.push({
        code: 'CLOSED_LOOP_MISMATCH',
        severity: 'exclude',
        reason: `Closed-loop card only works at ${card.closed_loop_merchant ?? 'its merchant'}.`,
      });
    }
  }

  // Credit capacity
  if (capacity === null) {
    fails.push({
      code: 'CAPACITY_UNKNOWN',
      severity: 'exclude',
      reason: 'Available credit has never been observed.',
    });
  } else if (amount > capacity) {
    fails.push({
      code: 'INSUFFICIENT_CREDIT',
      severity: 'exclude',
      reason: `Purchase exceeds estimated available credit (observed at ${card.eligibility.capacity.available_credit_observed_at}).`,
    });
  }

  // Interest / grace
  const g = graceEligible(card, query, settings);
  if (!g.ok) {
    fails.push({
      code: 'GRACE_NOT_ELIGIBLE',
      severity: 'exclude',
      reason: g.reason,
    });
  } else if (
    card.eligibility.grace.status !== 'active' &&
    (query.allow_interest_bearing || settings.allow_interest_bearing_recommendations)
  ) {
    fails.push({
      code: 'GRACE_OVERRIDE_ACTIVE',
      severity: 'penalize',
      reason: 'Interest-bearing override: full interest risk applied.',
    });
  }

  if (card.unresolved_past_due) {
    fails.push({
      code: 'DELINQUENT',
      severity: 'exclude',
      reason: 'Unresolved past-due cycle exists.',
    });
  }

  // Utilization
  const utilBps = postPurchaseUtilBps(card, amount);
  if (utilBps !== null) {
    if (utilBps > settings.utilization_hard_bps) {
      if (query.pay_before_statement_close === true) {
        warnings.push({
          code: 'UTILIZATION_SPIKE',
          message:
            'Utilization hard gate suppressed only because pay-before-close was explicitly confirmed on this query.',
        });
      } else {
        fails.push({
          code: 'UTILIZATION_HARD',
          severity: 'exclude',
          reason: `Post-purchase utilization ${utilBps} bps exceeds hard limit ${settings.utilization_hard_bps}.`,
        });
      }
    } else if (utilBps > settings.utilization_warning_bps) {
      fails.push({
        code: 'UTILIZATION_WARNING',
        severity: 'penalize',
        reason: `Post-purchase utilization ${utilBps} bps is in the warning band.`,
      });
    }
  }

  const age = card.eligibility.capacity.state_age_days;
  if (age !== null && age > settings.financial_staleness_days) {
    fails.push({
      code: 'STALE_STATE',
      severity: 'penalize',
      reason: `Financial observation is ${age} days old (TTL ${settings.financial_staleness_days}).`,
    });
    warnings.push({
      code: 'STALE_STATE',
      message: `Card state is ${age} days old; capacity gates may be based on stale data.`,
    });
  }

  return fails;
}

function ruleMatches(
  rule: RewardRuleInput,
  query: PurchaseQueryInput,
  category: SpendCategory,
  merchant: MerchantInput | null,
  asOf: string,
  forBonus: boolean,
): { match: boolean; soft: GateFailure[]; warnings: Warning[] } {
  const soft: GateFailure[] = [];
  const warnings: Warning[] = [];

  if (forBonus) {
    if (rule.category !== category) return { match: false, soft, warnings };
  } else if (rule.category !== null || rule.merchant_match !== 'any') {
    return { match: false, soft, warnings };
  }

  if (rule.channel !== 'any' && rule.channel !== query.channel && query.channel !== 'unknown') {
    return { match: false, soft, warnings };
  }
  if (rule.geography !== 'any' && rule.geography !== query.geography) {
    return { match: false, soft, warnings };
  }
  if (rule.valid_from !== null && asOf < rule.valid_from) return { match: false, soft, warnings };
  if (rule.valid_until !== null && asOf > rule.valid_until) {
    return { match: false, soft, warnings };
  }

  if (rule.merchant_match === 'portal_only') {
    if (category !== 'travel_portal') return { match: false, soft, warnings };
  }
  if (rule.merchant_match === 'named_merchant' || rule.merchant_match === 'merchant_list') {
    const names = (rule.merchant_names ?? []).map((n) => n.toLowerCase());
    const hay = `${query.merchant_input} ${merchant?.normalized_name ?? ''}`.toLowerCase();
    if (!names.some((n) => hay.includes(n))) return { match: false, soft, warnings };
  }
  if (rule.merchant_match === 'excluded_list') {
    const names = (rule.merchant_names ?? []).map((n) => n.toLowerCase());
    const hay = query.merchant_input.toLowerCase();
    if (names.some((n) => hay.includes(n))) return { match: false, soft, warnings };
  }

  if (rule.requires_activation && rule.activated !== true) {
    soft.push({
      code: 'ACTIVATION_MISSING',
      severity: 'penalize',
      reason: 'Rule requires activation and is not activated.',
    });
    warnings.push({
      code: 'ACTIVATION_MISSING',
      message: 'Activate this reward and re-run.',
    });
    return { match: false, soft, warnings };
  }
  if (rule.requires_membership && rule.membership_active !== true) {
    soft.push({
      code: 'MEMBERSHIP_INACTIVE',
      severity: 'penalize',
      reason: 'Membership not confirmed active.',
    });
    return { match: false, soft, warnings };
  }

  return { match: true, soft, warnings };
}

function effectiveRateBps(
  rule: RewardRuleInput,
  amount: Cents,
  caps: readonly CapPeriodInput[],
): number {
  if (rule.reward_rate_bps === null) return 0;
  const cap = caps.find((c) => c.reward_rule_id === rule.id);
  if (cap === undefined || rule.spend_cap_cents === null) return rule.reward_rate_bps;
  if (cap.remaining_cap_cents >= amount) return rule.reward_rate_bps;
  if (cap.remaining_cap_cents <= 0n) return rule.post_cap_rate_bps ?? 0;
  // Weighted effective rate for ranking among matches
  const head = Number(cap.remaining_cap_cents);
  const total = Number(amount);
  const rest = total - head;
  const post = rule.post_cap_rate_bps ?? 0;
  return Math.floor((head * rule.reward_rate_bps + rest * post) / total);
}

function pickBestRule(
  rules: readonly RewardRuleInput[],
  query: PurchaseQueryInput,
  category: SpendCategory,
  merchant: MerchantInput | null,
  asOf: string,
  forBonus: boolean,
  softOut: GateFailure[],
  warnOut: Warning[],
  caps: readonly CapPeriodInput[],
): RewardRuleInput | null {
  const matched: RewardRuleInput[] = [];
  for (const rule of rules) {
    const { match, soft, warnings } = ruleMatches(rule, query, category, merchant, asOf, forBonus);
    softOut.push(...soft);
    warnOut.push(...warnings);
    if (match) matched.push(rule);
  }
  if (matched.length === 0) return null;
  matched.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    const ar = effectiveRateBps(a, query.amount_cents, caps);
    const br = effectiveRateBps(b, query.amount_cents, caps);
    if (br !== ar) return br - ar;
    const ac = sourceConfidenceBps(a.confidence);
    const bc = sourceConfidenceBps(b.confidence);
    if (bc !== ac) return bc - ac;
    const ascope = a.scope === 'account_specific' ? 1 : 0;
    const bscope = b.scope === 'account_specific' ? 1 : 0;
    if (bscope !== ascope) return bscope - ascope;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return matched[0] ?? null;
}

function rewardFromRule(
  rule: RewardRuleInput | null,
  amount: Cents,
  caps: readonly CapPeriodInput[],
  soft: GateFailure[],
  warnings: Warning[],
): Cents {
  if (rule === null) return asCents(0n);

  if (rule.milli_points_per_dollar !== null) {
    if (rule.milli_cents_per_point === null) {
      warnings.push({
        code: 'CANNOT_VALUE_POINTS',
        message: 'Points rule has no verified milli_cents_per_point; reward valued at 0.',
      });
      return asCents(0n);
    }
    return pointsRewardCents(
      amount,
      asMilliPointsPerDollar(rule.milli_points_per_dollar),
      asMilliCentsPerPoint(rule.milli_cents_per_point),
    );
  }

  if (rule.reward_rate_bps === null) return asCents(0n);

  const rate = asBps(rule.reward_rate_bps);
  const cap = caps.find((c) => c.reward_rule_id === rule.id);
  if (cap === undefined || rule.spend_cap_cents === null) {
    return cashbackCents(amount, rate);
  }

  const headroom = cap.remaining_cap_cents;
  if (headroom <= 0n) {
    soft.push({
      code: 'CAP_EXHAUSTED',
      severity: 'penalize',
      reason: 'Cap exhausted; using post_cap_rate_bps or zero.',
    });
    warnings.push({ code: 'CAP_EXHAUSTED', message: 'Category cap is exhausted.' });
    if (rule.post_cap_rate_bps !== null) {
      return cashbackCents(amount, asBps(rule.post_cap_rate_bps));
    }
    return asCents(0n);
  }

  if (headroom >= amount) {
    return cashbackCents(amount, rate);
  }

  soft.push({
    code: 'CAP_PARTIAL',
    severity: 'penalize',
    reason: 'Purchase exceeds remaining cap headroom; split calculation applied.',
  });
  const fallback = rule.post_cap_rate_bps ?? 0;
  const head = cashbackCents(headroom, rate);
  const rest = cashbackCents(asCents(amount - headroom), asBps(fallback));
  return asCents(head + rest);
}

function ruleConfidenceBps(rule: RewardRuleInput, asOf: BusinessDate, conflictBps: number): number {
  const tier = sourceConfidenceBps(rule.confidence);
  const age = ageDays(asOf, rule.last_verified_at);
  const stale = age === null ? 10000 : stalenessMultiplierBps(age, REWARD_RULE_TTL_DAYS);
  return minBps(tier, stale, conflictBps);
}

function offerValue(
  offers: readonly OfferInput[],
  cardId: string,
  query: PurchaseQueryInput,
  asOf: BusinessDate,
  soft: GateFailure[],
  warnings: Warning[],
): { value: Cents; confidenceBps: number } {
  let total = 0n;
  let conf = 10000;
  let any = false;

  for (const o of offers) {
    if (o.card_id !== cardId) continue;
    if (asOf < o.valid_from || asOf > o.valid_until) {
      soft.push({
        code: 'OFFER_EXPIRED',
        severity: 'penalize',
        reason: 'Offer outside validity window.',
      });
      continue;
    }
    if (o.activation_required && !o.activated) {
      warnings.push({
        code: 'ACTIVATION_MISSING',
        message: 'Offer is not activated — activate and re-run.',
      });
      continue;
    }
    if (o.minimum_spend_cents !== null && query.amount_cents < o.minimum_spend_cents) continue;
    if (o.channel !== 'any' && o.channel !== query.channel) continue;

    // Offers fail closed past TTL (docs/02 §2)
    const age = ageDays(asOf, o.last_verified_at);
    if (age !== null && age > OFFER_TTL_DAYS) {
      soft.push({
        code: 'OFFER_EXPIRED',
        severity: 'penalize',
        reason: 'Offer activation state past TTL; treated as inactive.',
      });
      continue;
    }

    let v = 0n;
    if (o.value_cents !== null) v = o.value_cents;
    else if (o.value_bps !== null) v = cashbackCents(query.amount_cents, asBps(o.value_bps));
    if (o.maximum_reward_cents !== null && v > o.maximum_reward_cents) {
      v = o.maximum_reward_cents;
    }
    total += v;
    const offerConf = minBps(
      sourceConfidenceBps(o.confidence),
      age === null ? 10000 : stalenessMultiplierBps(age, OFFER_TTL_DAYS),
    );
    conf = any ? minBps(conf, offerConf) : offerConf;
    any = true;
  }

  return { value: asCents(total), confidenceBps: any ? conf : 10000 };
}

function interestRisk(
  card: RankCardInput,
  query: PurchaseQueryInput,
  settings: SettingsInput,
  warnings: Warning[],
): Cents {
  if (card.eligibility.grace.status === 'active') return asCents(0n);

  let apr = card.purchase_apr_bps;
  if (apr === null) {
    apr = settings.assumed_worst_apr_bps;
    warnings.push({
      code: 'APR_UNKNOWN',
      message: 'Purchase APR unknown; using assumed_worst_apr_bps for interest risk.',
    });
  }
  return monthlyInterestCents(query.amount_cents, asBps(apr));
}

function protectionTier(benefits: readonly BenefitInput[], cardId: string): number {
  const mine = benefits.filter((b) => b.card_id === cardId && !b.confirmed_absent);
  const has = (k: BenefitInput['kind']) => mine.some((b) => b.kind === k);
  const absent = (k: BenefitInput['kind']) =>
    benefits.some((b) => b.card_id === cardId && b.kind === k && b.confirmed_absent);
  if (has('purchase_protection') && has('extended_warranty')) return 1;
  if (has('purchase_protection') || has('extended_warranty')) return 2;
  if (absent('purchase_protection') && absent('extended_warranty')) return 4;
  return 3;
}

function travelTier(benefits: readonly BenefitInput[], cardId: string): number {
  const mine = benefits.filter((b) => b.card_id === cardId && !b.confirmed_absent);
  const cdw = mine.find((b) => b.kind === 'rental_car_cdw');
  const trip = mine.some(
    (b) =>
      b.kind === 'trip_cancellation' || b.kind === 'trip_interruption' || b.kind === 'trip_delay',
  );
  if (cdw?.position === 'primary' && trip) return 1;
  if (cdw !== undefined) return 2;
  return 3;
}

function priorityTier(
  mode: PriorityMode,
  card: RankCardInput,
  benefits: readonly BenefitInput[],
  utilBps: number | null,
  soft: GateFailure[],
  rewardConfBps: number,
): number {
  switch (mode) {
    case 'maximize_rewards':
      return 1;
    case 'maximize_protection':
    case 'large_purchase':
      return protectionTier(benefits, card.card_id);
    case 'travel_coverage':
      return travelTier(benefits, card.card_id);
    case 'minimize_utilization': {
      const u = utilBps ?? 9999;
      if (u < 1000) return 1;
      if (u < 3000) return 2;
      if (u < 8000) return 3;
      return 4;
    }
    case 'lowest_risk': {
      const fresh = (card.eligibility.capacity.state_age_days ?? 999) <= 7;
      const graceOk = card.eligibility.grace.status === 'active';
      const confOk = rewardConfBps >= sourceConfidenceBps('current_statement');
      return graceOk && fresh && confOk ? 1 : 2;
    }
    case 'international': {
      if (card.foreign_txn_fee_bps === 0) return 1;
      if (card.foreign_txn_fee_bps === null) return 2;
      return 3;
    }
    case 'financing_needed':
      return 3;
    case 'business_expense':
      return card.owner === 'business' ? 1 : 2;
    case 'recurring_subscription': {
      if (card.disposition === 'review_for_closure' || card.disposition === 'close_when_safe') {
        return 2;
      }
      return 1;
    }
    case 'avoid_annual_fee': {
      if (card.annual_fee_cents !== null && card.annual_fee_cents > 0n) {
        soft.push({
          code: 'ANNUAL_FEE_AVOIDANCE',
          severity: 'penalize',
          reason: 'Card has an annual fee under avoid_annual_fee mode.',
        });
        return 2;
      }
      return 1;
    }
  }
}

function formatRuleRate(rule: RewardRuleInput | null): string | null {
  if (rule === null) return null;
  if (rule.reward_rate_bps !== null) return formatBpsAsPercent(rule.reward_rate_bps);
  if (rule.milli_points_per_dollar !== null) {
    const milli = rule.milli_points_per_dollar;
    const whole = Math.floor(milli / 1000);
    const frac = milli % 1000;
    if (frac === 0) return `${whole} pts/$`;
    const fracStr = String(frac).padStart(3, '0').replace(/0+$/, '');
    return `${whole}.${fracStr} pts/$`;
  }
  return null;
}

function prePurchaseUtilBps(card: RankCardInput): number | null {
  const limit = card.eligibility.state.observed.credit_limit_cents;
  if (limit === null || limit <= 0n) return null;
  const posted = card.eligibility.state.estimated_posted_balance ?? 0n;
  return Number((posted * 10000n) / limit);
}

function statementCloseDate(card: RankCardInput): string | null {
  return (
    card.eligibility.state.observed.next_closing_date ??
    card.eligibility.state.observed.statement_closing_date
  );
}

function confirmedTierBenefit(
  mode: PriorityMode,
  benefits: readonly BenefitInput[],
  cardId: string,
): string | null {
  const mine = benefits.filter((b) => b.card_id === cardId && !b.confirmed_absent);
  if (mode === 'travel_coverage') {
    if (mine.some((b) => b.kind === 'rental_car_cdw' && b.position === 'primary')) {
      return 'rental_car_cdw';
    }
    if (mine.some((b) => b.kind === 'rental_car_cdw')) return 'rental_car_cdw';
    return null;
  }
  if (mode === 'maximize_protection' || mode === 'large_purchase') {
    const hasPp = mine.some((b) => b.kind === 'purchase_protection');
    const hasEw = mine.some((b) => b.kind === 'extended_warranty');
    if (hasPp && hasEw) return 'purchase_protection';
    if (hasPp) return 'purchase_protection';
    if (hasEw) return 'extended_warranty';
    return null;
  }
  return null;
}

function emptyTrace(card: RankCardInput, resolved: ResolvedCategory): ScoreTrace {
  return {
    card_id: card.card_id,
    category_resolved: resolved.category,
    category_source: resolved.source,
    category_confidence_bps: resolved.confidence_bps,
    category_uncertain: resolved.uncertain,
    matched_base_rule_id: null,
    matched_bonus_rule_id: null,
    base_reward_cents: asCents(0n),
    category_bonus_cents: asCents(0n),
    offer_value_cents: asCents(0n),
    financing_value_cents: asCents(0n),
    base_reward_confidence_bps: 0,
    bonus_reward_confidence_bps: 0,
    offer_confidence_bps: 0,
    discounted_reward_cents: asCents(0n),
    discounted_offer_cents: asCents(0n),
    fx_fee_cents: asCents(0n),
    interest_risk_cents: asCents(0n),
    expected_net_cents: asCents(0n),
    effective_reward_confidence_bps: 0,
    post_purchase_utilization_bps: null,
    state_age_days: card.eligibility.capacity.state_age_days,
    grace_status: card.eligibility.grace.status,
    tokens: [],
    ...emptyExplanationSlots(),
  };
}

function scoreCard(
  card: RankCardInput,
  input: RankCardsInput,
  resolved: ResolvedCategory,
  softExtra: GateFailure[],
  warnings: Warning[],
): { trace: ScoreTrace; soft: GateFailure[]; utilBps: number | null; rewardConf: number } {
  const soft: GateFailure[] = [...softExtra];
  const rules = input.rewardRules.filter((r) => r.card_id === card.card_id);
  const asOf = businessDate(input.asOf);

  const baseRule = pickBestRule(
    rules,
    input.query,
    resolved.category,
    input.merchant,
    input.asOf,
    false,
    soft,
    warnings,
    input.capPeriods,
  );

  let bonusRule: RewardRuleInput | null = null;
  if (resolved.intermediary) {
    warnings.push({
      code: 'MERCHANT_INTERMEDIARY',
      message: 'Payment intermediary: category-bonus rules gated off.',
    });
  } else {
    bonusRule = pickBestRule(
      rules,
      input.query,
      resolved.category,
      input.merchant,
      input.asOf,
      true,
      soft,
      warnings,
      input.capPeriods,
    );
  }

  const baseReward = rewardFromRule(
    baseRule,
    input.query.amount_cents,
    input.capPeriods,
    soft,
    warnings,
  );
  const bonusGross = rewardFromRule(
    bonusRule,
    input.query.amount_cents,
    input.capPeriods,
    soft,
    warnings,
  );
  const categoryBonus = asCents(bonusGross > baseReward ? bonusGross - baseReward : 0n);

  let catConf = resolved.confidence_bps;
  if (resolved.uncertain) {
    // Additional ×7000 when resolution is unknown/inferred (docs/03 §5)
    catConf = bpsProduct(catConf === 0 ? 3500 : catConf, 7000);
    warnings.push({
      code: 'CATEGORY_UNCERTAIN',
      message:
        'Category resolution is uncertain; only the category-dependent bonus is further discounted.',
    });
  }

  if (
    input.query.financing_requested &&
    baseRule !== null &&
    baseRule.mutually_exclusive_with.some((x) => x.toLowerCase().includes('financ'))
  ) {
    soft.push({
      code: 'FINANCING_CONFLICT',
      severity: 'penalize',
      reason: 'Financing requested and reward rule is mutually exclusive with financing.',
    });
  }

  const offer = offerValue(input.offers, card.card_id, input.query, asOf, soft, warnings);

  let financing = asCents(0n);
  if (input.query.financing_requested) {
    financing = asCents(0n); // verified true 0% only — deferred interest is never financing_value
    warnings.push({
      code: 'DEFERRED_INTEREST_RISK',
      message:
        'Financing requested: deferred-interest promos contribute 0 financing_value; retroactive exposure must be quantified separately.',
    });
    warnings.push({
      code: 'REWARDS_VS_FINANCING_CHOICE',
      message: 'Financing vs rewards tradeoff — compare explicitly before choosing.',
    });
  }

  let fx = asCents(0n);
  const fxBps = card.foreign_txn_fee_bps;
  if (input.query.geography === 'international' && fxBps !== null && fxBps > 0) {
    fx = fxFeeCents(input.query.amount_cents, asBps(fxBps));
    soft.push({
      code: 'FX_FEE',
      severity: 'penalize',
      reason: 'Foreign transaction fee subtracted at full value.',
    });
  }

  const interest = interestRisk(card, input.query, input.settings, warnings);

  const conflictBps = 10000;
  const baseConfBps = baseRule ? ruleConfidenceBps(baseRule, asOf, conflictBps) : 0;
  const bonusConfBps = bonusRule ? ruleConfidenceBps(bonusRule, asOf, conflictBps) : 0;

  // docs/03: reward_confidence = min(...) of the matched reward rule(s)
  const rewardConf = bonusRule ? minBps(baseConfBps || 0, bonusConfBps) : baseConfBps;

  if (baseRule && sourceConfidenceBps(baseRule.confidence) === 0) {
    warnings.push({
      code: 'UNVERIFIED_REWARD_RATE',
      message: 'Reward rule confidence is unverified; expected reward discounted to zero.',
    });
  }

  // discounted_reward = round(base × reward_conf) + round(bonus × reward_conf × category_conf)
  const discReward = asCents(
    applyBps(baseReward, rewardConf) + applyBps(categoryBonus, bpsProduct(rewardConf, catConf)),
  );
  const discOffer = applyBps(offer.value, offer.confidenceBps);
  const expectedNet = asCents(discReward + discOffer + financing - fx - interest);

  const utilBps = postPurchaseUtilBps(card, input.query.amount_cents);
  const utilBefore = prePurchaseUtilBps(card);
  const closeDate = statementCloseDate(card);
  const slots = emptyExplanationSlots();
  const tokens: string[] = [];

  const displayRate = formatRuleRate(bonusRule ?? baseRule);
  const baseRateDisplay = formatRuleRate(baseRule);

  if (expectedNet > 0n && displayRate !== null) {
    tokens.push('WON_ON_RATE');
    slots.slot_rate = displayRate;
  }

  if (offer.value > 0n && baseRateDisplay !== null) {
    tokens.push('WON_ON_OFFER');
    slots.slot_offer_value = formatCentsDisplay(offer.value);
    slots.slot_base_rate = baseRateDisplay;
  }

  const graceDue = card.eligibility.grace.paid_in_full_due_date;
  if (card.eligibility.grace.status === 'active' && graceDue !== null) {
    tokens.push('GRACE_OK');
    slots.slot_grace_due_date = graceDue;
  }

  const avail = card.eligibility.capacity.estimated_available_credit;
  if (avail !== null && input.query.amount_cents <= avail) {
    tokens.push('CREDIT_OK');
    slots.slot_available_credit = formatCentsDisplay(avail);
    slots.slot_purchase_amount = formatCentsDisplay(input.query.amount_cents);
  }

  if (utilBefore !== null && utilBps !== null && closeDate !== null) {
    tokens.push('UTIL_EFFECT');
    slots.slot_util_before = formatBpsAsPercent(utilBefore);
    slots.slot_util_after = formatBpsAsPercent(utilBps);
    slots.slot_statement_close_date = closeDate;
  }

  if (
    input.query.geography === 'international' &&
    fxBps === 0 &&
    card.foreign_txn_fee_verified_at !== null
  ) {
    tokens.push('FX_OK');
    slots.slot_fx_verified_date = card.foreign_txn_fee_verified_at;
  }

  const trace: ScoreTrace = {
    card_id: card.card_id,
    category_resolved: resolved.category,
    category_source: resolved.source,
    category_confidence_bps: catConf,
    category_uncertain: resolved.uncertain,
    matched_base_rule_id: baseRule?.id ?? null,
    matched_bonus_rule_id: bonusRule?.id ?? null,
    base_reward_cents: baseReward,
    category_bonus_cents: categoryBonus,
    offer_value_cents: offer.value,
    financing_value_cents: financing,
    base_reward_confidence_bps: baseConfBps,
    bonus_reward_confidence_bps: bonusConfBps,
    offer_confidence_bps: offer.confidenceBps,
    discounted_reward_cents: discReward,
    discounted_offer_cents: discOffer,
    fx_fee_cents: fx,
    interest_risk_cents: interest,
    expected_net_cents: expectedNet,
    effective_reward_confidence_bps: rewardConf,
    post_purchase_utilization_bps: utilBps,
    state_age_days: card.eligibility.capacity.state_age_days,
    grace_status: card.eligibility.grace.status,
    tokens,
    ...slots,
  };

  return { trace, soft, utilBps, rewardConf };
}

function compareRanked(a: RankedCard, b: RankedCard): number {
  if (a.tier !== b.tier) return a.tier - b.tier;
  if (b.trace.expected_net_cents !== a.trace.expected_net_cents) {
    return b.trace.expected_net_cents > a.trace.expected_net_cents ? 1 : -1;
  }
  if (b.trace.effective_reward_confidence_bps !== a.trace.effective_reward_confidence_bps) {
    return b.trace.effective_reward_confidence_bps - a.trace.effective_reward_confidence_bps;
  }
  const ua = a.trace.post_purchase_utilization_bps ?? 99999;
  const ub = b.trace.post_purchase_utilization_bps ?? 99999;
  if (ua !== ub) return ua - ub;
  const ageA = a.trace.state_age_days ?? 99999;
  const ageB = b.trace.state_age_days ?? 99999;
  if (ageA !== ageB) return ageA - ageB;
  // spend_policy eligible preferred — callers put that on RankedCard via gate absence;
  // lexicographic card id last
  return a.card_id < b.card_id ? -1 : a.card_id > b.card_id ? 1 : 0;
}

function pushGraceWarnings(card: RankCardInput, warnings: Warning[]): void {
  const status = card.eligibility.grace.status;
  if (status === 'unknown') {
    warnings.push({
      code: 'GRACE_UNKNOWN',
      message:
        'Current balance is zero, but the grace period on new purchases has not been confirmed. If this account carried a balance recently, a full statement cycle paid in full may be required before new purchases are interest-free. Verify with the issuer before using this card.',
    });
  } else if (status === 'restoration_pending') {
    warnings.push({
      code: 'GRACE_RESTORATION_PENDING',
      message: 'Grace restoration is still pending additional qualifying cycles.',
    });
  }
}

/**
 * Run Stages 0–8. Identical inputs produce identical outputs (bigint-safe deep equality).
 */
export function rankCards(input: RankCardsInput): RecommendationOutput {
  const warnings: Warning[] = [];
  const resolved = resolveCategory(input.query, input.merchant, input.categoryObservations);
  const eligible: RankedCard[] = [];
  const ineligible: RankedCard[] = [];

  for (const card of input.cards) {
    const gates = hardGates(card, input.query, input.settings, warnings);
    const excluded = gates.filter((g) => g.severity === 'exclude');
    const softGates = gates.filter((g) => g.severity === 'penalize');

    if (excluded.length > 0) {
      pushGraceWarnings(card, warnings);
      ineligible.push({
        card_id: card.card_id,
        eligible: false,
        tier: 99,
        gate_failures: gates,
        trace: emptyTrace(card, resolved),
        explanation: excluded.map((e) => e.reason),
      });
      continue;
    }

    const scored = scoreCard(card, input, resolved, softGates, warnings);
    const tier = priorityTier(
      input.query.mode,
      card,
      input.benefits,
      scored.utilBps,
      scored.soft,
      scored.rewardConf,
    );
    eligible.push({
      card_id: card.card_id,
      eligible: true,
      tier,
      gate_failures: [...gates.filter((g) => g.severity === 'penalize'), ...scored.soft],
      trace: scored.trace,
      explanation: [], // filled after winner-only tokens are attached
    });
  }

  eligible.sort(compareRanked);

  // Winner-only Stage 7 tokens: WON_ON_TIER + RUNNER_UP (need the sorted list).
  const winnerCard = eligible[0];
  const runnerUp = eligible[1];
  if (winnerCard !== undefined) {
    const tokens = [...winnerCard.trace.tokens];
    let slot_benefit = winnerCard.trace.slot_benefit;
    let slot_mode = winnerCard.trace.slot_mode;
    let slot_runner_up_card = winnerCard.trace.slot_runner_up_card;
    let slot_runner_up_delta = winnerCard.trace.slot_runner_up_delta;
    let slot_runner_up_differentiator = winnerCard.trace.slot_runner_up_differentiator;

    const benefit = confirmedTierBenefit(input.query.mode, input.benefits, winnerCard.card_id);
    if (benefit !== null && (runnerUp === undefined || winnerCard.tier < runnerUp.tier)) {
      tokens.push('WON_ON_TIER');
      slot_benefit = benefit;
      slot_mode = input.query.mode;
    }

    if (runnerUp !== undefined) {
      const delta = winnerCard.trace.expected_net_cents - runnerUp.trace.expected_net_cents;
      if (delta > 0n) {
        tokens.push('RUNNER_UP');
        slot_runner_up_card = runnerUp.card_id;
        slot_runner_up_delta = formatCentsDisplay(delta);
        slot_runner_up_differentiator =
          runnerUp.trace.matched_bonus_rule_id !== winnerCard.trace.matched_bonus_rule_id
            ? 'Different verified category bonus.'
            : 'Lower expected net after confidence discounts.';
      }
    }

    winnerCard.trace = {
      ...winnerCard.trace,
      tokens,
      slot_benefit,
      slot_mode,
      slot_runner_up_card,
      slot_runner_up_delta,
      slot_runner_up_differentiator,
    };
  }

  for (const row of eligible) {
    row.explanation = generateExplanation(row.trace);
  }

  const allUnverified =
    eligible.length > 0 &&
    eligible.every(
      (c) =>
        c.trace.effective_reward_confidence_bps === 0 &&
        c.trace.discounted_reward_cents === 0n &&
        c.trace.discounted_offer_cents === 0n,
    );

  if (allUnverified) {
    warnings.push({
      code: 'INSUFFICIENT_EVIDENCE',
      message: 'Insufficient evidence to recommend — ranked list has zero expected values.',
    });
  }

  const noEligible = eligible.length === 0;
  if (noEligible) {
    warnings.push({
      code: 'NO_CAPACITY',
      message: 'No eligible card remains after hard gates.',
    });
  }

  const winner = allUnverified ? null : (eligible[0]?.card_id ?? null);

  let cross: string | null = null;
  if (winner !== null && input.query.mode === 'maximize_rewards') {
    const alt = [...eligible].sort((a, b) => {
      const ta = protectionTier(input.benefits, a.card_id);
      const tb = protectionTier(input.benefits, b.card_id);
      if (ta !== tb) return ta - tb;
      return compareRanked(a, b);
    });
    const altWin = alt[0]?.card_id;
    if (altWin !== undefined && altWin !== winner) {
      cross = `Under maximize-protection, ${altWin} would win instead.`;
    }
  }

  return {
    mode: input.query.mode,
    category_resolved: resolved.category,
    category_source: resolved.source,
    eligible,
    ineligible,
    winner_card_id: winner,
    cross_mode_note: cross,
    no_eligible_card: noEligible || allUnverified,
    warnings: dedupeWarnings(warnings),
  };
}

/** Keep first occurrence of each code+message (per-card scoring must not spam the UI). */
function dedupeWarnings(warnings: readonly Warning[]): Warning[] {
  const seen = new Set<string>();
  const out: Warning[] = [];
  for (const w of warnings) {
    const key = `${w.code}\0${w.message}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(w);
  }
  return out;
}
